export type PdfAvailabilityStatus = 'unknown' | 'expected' | 'verified' | 'unavailable';

export type PdfAvailabilitySource =
    | 'stored'
    | 'pmc_cloud'
    | 'europe_pmc'
    | 'unpaywall';

export interface PdfAvailabilityFields {
    pdf_availability_status?: string | null;
    pdf_availability_source?: string | null;
    pdf_url?: string | null;
    pdf_availability_checked_at?: string | null;
    pdf_availability_next_check_at?: string | null;
}

export interface PubmedPdfAvailabilityDecision {
    status: PdfAvailabilityStatus;
    source: PdfAvailabilitySource | null;
    canGenerateSummary: boolean;
    effectivePdfUrl: string | null;
    isChecking: boolean;
    isUnavailable: boolean;
}

export type UnpaywallPdfStatus =
    | 'idle'
    | 'checking'
    | 'available'
    | 'unavailable'
    | 'unknown';

export interface UnpaywallPdfResult {
    status: UnpaywallPdfStatus;
    isChecking: boolean;
    isUnavailable: boolean;
    isUnknown: boolean;
    oaPdfUrl: string | null;
}

const PDF_AVAILABILITY_REQUIRED_CODE = 'PDF_AVAILABILITY_REQUIRED';

export function isPdfAvailabilityRequiredError(error: unknown): boolean {
    const candidate = error as {
        message?: unknown;
        details?: unknown;
        hint?: unknown;
    } | null;
    return [candidate?.message, candidate?.details, candidate?.hint]
        .some((value) => (
            typeof value === 'string'
            && value.includes(PDF_AVAILABILITY_REQUIRED_CODE)
        ));
}

export function pdfAvailabilityRequiredMessage(): string {
    // i18n-ignore: technical-token -- stable service error code; the paper view owns localized copy.
    return PDF_AVAILABILITY_REQUIRED_CODE;
}

const AVAILABLE_STATUSES = new Set<PdfAvailabilityStatus>(['expected', 'verified']);
const KNOWN_STATUSES = new Set<PdfAvailabilityStatus>([
    'unknown',
    'expected',
    'verified',
    'unavailable',
]);
const KNOWN_SOURCES = new Set<PdfAvailabilitySource>([
    'stored',
    'pmc_cloud',
    'europe_pmc',
    'unpaywall',
]);
const OFFICIAL_REMOTE_SOURCES = new Set<PdfAvailabilitySource>([
    'pmc_cloud',
    'europe_pmc',
    'unpaywall',
]);

export function normalizeHttpsPdfCandidate(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }
    const candidate = value.trim();
    if (!candidate) {
        return null;
    }
    try {
        const parsed = new URL(candidate);
        return parsed.protocol === 'https:'
            && Boolean(parsed.hostname)
            && !parsed.username
            && !parsed.password
            ? candidate
            : null;
    } catch {
        return null;
    }
}

export function canGenerateSummaryFromPersistedPdf(
    persistedPdfUrl: string | null | undefined,
): boolean {
    return typeof persistedPdfUrl === 'string' && Boolean(persistedPdfUrl.trim());
}

export function unpaywallRequestKey({
    doi,
    hasPdfUrl,
    enabled,
}: {
    doi: string | null | undefined;
    hasPdfUrl: boolean;
    enabled: boolean;
}): string {
    return `${enabled ? 'enabled' : 'disabled'}:${hasPdfUrl ? 'stored' : 'missing'}:${doi?.trim() || ''}`;
}

export function initialUnpaywallPdfResult({
    doi,
    hasPdfUrl,
    enabled,
}: {
    doi: string | null | undefined;
    hasPdfUrl: boolean;
    enabled: boolean;
}): UnpaywallPdfResult {
    if (!enabled || hasPdfUrl) {
        return {
            status: 'idle',
            isChecking: false,
            isUnavailable: false,
            isUnknown: false,
            oaPdfUrl: null,
        };
    }

    if (!doi?.trim()) {
        return {
            status: 'unknown',
            isChecking: false,
            isUnavailable: false,
            isUnknown: true,
            oaPdfUrl: null,
        };
    }

    return {
        status: 'checking',
        isChecking: true,
        isUnavailable: false,
        isUnknown: false,
        oaPdfUrl: null,
    };
}

export function unknownUnpaywallPdfResult(): UnpaywallPdfResult {
    return {
        status: 'unknown',
        isChecking: false,
        isUnavailable: false,
        isUnknown: true,
        oaPdfUrl: null,
    };
}

/**
 * Interpret a successful Unpaywall response as a view-only link signal.
 * A landing page is deliberately not a PDF candidate, and this result must
 * never be used by itself to authorize backend summary generation.
 */
export function parseUnpaywallPdfResponse(payload: unknown): UnpaywallPdfResult {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return unknownUnpaywallPdfResult();
    }

    const value = payload as Record<string, unknown>;
    if (value.is_oa !== true && value.is_oa !== false) {
        return unknownUnpaywallPdfResult();
    }

    if (value.is_oa === false) {
        return {
            status: 'unavailable',
            isChecking: false,
            isUnavailable: true,
            isUnknown: false,
            oaPdfUrl: null,
        };
    }

    const rawLocations = [
        value.best_oa_location,
        ...(Array.isArray(value.oa_locations) ? value.oa_locations : []),
    ];
    for (const rawLocation of rawLocations) {
        if (!rawLocation || typeof rawLocation !== 'object' || Array.isArray(rawLocation)) {
            continue;
        }
        const location = rawLocation as Record<string, unknown>;
        const pdfUrl = normalizeHttpsPdfCandidate(location.url_for_pdf);
        if (pdfUrl) {
            return {
                status: 'available',
                isChecking: false,
                isUnavailable: false,
                isUnknown: false,
                oaPdfUrl: pdfUrl,
            };
        }
    }

    // An OA record without an exact usable PDF is not proof that no PDF can be
    // obtained. Keep the UI honest and distinguish this from `is_oa: false`.
    return unknownUnpaywallPdfResult();
}

function parsedTimestamp(value: string | null | undefined): number | null {
    if (!value) {
        return null;
    }
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : null;
}

export function isPdfAvailabilityDueNow(
    nextCheckAt: string | null | undefined,
    nowMs = Date.now(),
): boolean {
    if (!nextCheckAt) {
        return true;
    }
    const timestamp = parsedTimestamp(nextCheckAt);
    // A malformed backend timestamp should not permanently suppress the one
    // bounded repair request. The backend remains authoritative and fail-closed.
    return timestamp === null || timestamp <= nowMs;
}

export function shouldRetryDueUnknownPdfAvailability(
    fields: PdfAvailabilityFields,
    completedAttempts: number,
    maxAttempts = 2,
    nowMs = Date.now(),
): boolean {
    return fields.pdf_availability_status === 'unknown'
        && completedAttempts > 0
        && completedAttempts < maxAttempts
        && isPdfAvailabilityDueNow(fields.pdf_availability_next_check_at, nowMs);
}

/**
 * Keep an on-demand response from masking a newer paper row received through
 * a parent refetch. Timestamps are authoritative; without usable timestamps,
 * prefer a non-unknown parent decision over a cached unknown response.
 */
export function selectFreshestPdfAvailability<T extends PdfAvailabilityFields>(
    paper: T,
    refreshed: PdfAvailabilityFields | null | undefined,
): T & PdfAvailabilityFields {
    if (!refreshed) {
        return paper;
    }

    const paperCheckedAt = parsedTimestamp(paper.pdf_availability_checked_at);
    const refreshedCheckedAt = parsedTimestamp(refreshed.pdf_availability_checked_at);
    if (
        paperCheckedAt !== null
        && (refreshedCheckedAt === null || paperCheckedAt > refreshedCheckedAt)
    ) {
        return paper;
    }
    if (
        paperCheckedAt === null
        && refreshedCheckedAt === null
        && paper.pdf_availability_status
        && paper.pdf_availability_status !== 'unknown'
        && refreshed.pdf_availability_status === 'unknown'
    ) {
        return paper;
    }

    return { ...paper, ...refreshed };
}

/**
 * Interpret the backend-owned PubMed PDF decision. Unknown or malformed values
 * deliberately fail closed so speculative PMCID URLs never enable summaries.
 */
export function resolvePubmedPdfAvailability(
    paper: PdfAvailabilityFields,
): PubmedPdfAvailabilityDecision {
    const rawStatus = paper.pdf_availability_status;
    const parsedStatus = rawStatus && KNOWN_STATUSES.has(rawStatus as PdfAvailabilityStatus)
        ? rawStatus as PdfAvailabilityStatus
        : 'unknown';
    const rawSource = paper.pdf_availability_source;
    const source = rawSource && KNOWN_SOURCES.has(rawSource as PdfAvailabilitySource)
        ? rawSource as PdfAvailabilitySource
        : null;
    const pdfUrl = normalizeHttpsPdfCandidate(paper.pdf_url);
    const hasNoPdfUrl = paper.pdf_url == null;
    const isValidDecision = (
        parsedStatus === 'verified'
        && source === 'stored'
        && (hasNoPdfUrl || pdfUrl !== null)
    ) || (
        parsedStatus === 'expected'
        && source !== null
        && OFFICIAL_REMOTE_SOURCES.has(source)
        && pdfUrl !== null
    ) || (
        (parsedStatus === 'unknown' || parsedStatus === 'unavailable')
        && source === null
        && hasNoPdfUrl
    );
    const status = isValidDecision ? parsedStatus : 'unknown';
    const effectiveSource = isValidDecision ? source : null;
    const isAvailable = AVAILABLE_STATUSES.has(status) && effectiveSource !== null;

    return {
        status,
        source: effectiveSource,
        canGenerateSummary: isAvailable,
        effectivePdfUrl: isAvailable ? pdfUrl : null,
        // Only the hook knows whether a refresh request is actually in flight.
        isChecking: false,
        isUnavailable: status === 'unavailable',
    };
}

export function withPdfAvailabilityRefreshState(
    decision: PubmedPdfAvailabilityDecision,
    isRefreshInFlight: boolean,
): PubmedPdfAvailabilityDecision {
    return {
        ...decision,
        isChecking: decision.status === 'unknown' && isRefreshInFlight,
    };
}
